import React, { FC, useEffect, useRef, useState } from "react"
import clsx from "clsx"

import { ColorPickerField } from "components/inputs/ColorPickerField"
import { slugify } from "components/promotions/BlocksEditor/helpers/slugify"

import { Tag, TagWithUsage } from "services/tags/type"

type Props = {
  saving: boolean
  editTag: TagWithUsage | null
  onSave: (payload: Partial<Tag>) => Promise<void>
}

export const ManualTagTab: FC<Props> = ({ saving, editTag, onSave }) => {
  const isEdit = !!editTag?.id

  const [name, setName] = useState("")
  const [slug, setSlug] = useState("")
  const [color, setColor] = useState("#64748B")
  const [category, setCategory] = useState("")
  const [priority, setPriority] = useState<number>(0)
  const [description, setDescription] = useState("")

  const slugEditedRef = useRef(false)

  useEffect(() => {
    if (editTag) {
      setName(editTag.name ?? "")
      setSlug(editTag.slug ?? "")
      setColor(editTag.color ?? "#64748B")
      setCategory(editTag.category ?? "")
      setPriority(editTag.priority ?? 0)
      setDescription(editTag.description ?? "")
      slugEditedRef.current = true
    } else {
      setName("")
      setSlug("")
      setColor("#64748B")
      setCategory("")
      setPriority(0)
      setDescription("")
      slugEditedRef.current = false
    }
  }, [editTag])

  const canSave = name.trim().length > 0

  const handleSave = () => {
    if (!canSave) return

    const payload: Partial<Tag> = {
      name: name.trim(),
      color,
      category: category.trim() || null,
      priority: Number.isFinite(priority) ? Number(priority) : 0,
      description: description.trim() || null,
      isAutomatic: false
    }

    if (!isEdit) payload.slug = slugify((slug || name).trim()) || undefined

    onSave(payload)
  }

  const inputCls =
    "input input-bordered w-full bg-white dark:bg-[#0C0C1A] text-black dark:text-white border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)]"
  const textareaCls =
    "textarea textarea-bordered w-full bg-white dark:bg-[#0C0C1A] text-black dark:text-white border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)]"

  return (
    <div className="mt-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-sm opacity-80 mb-1">Name *</div>
          <input
            className={inputCls}
            value={name}
            onChange={(e) => {
              const nextName = e.target.value
              setName(nextName)
              if (!slugEditedRef.current) setSlug(slugify(nextName))
            }}
          />
        </div>

        <div>
          <div className="text-sm opacity-80 mb-1">Slug</div>
          <input
            className={clsx(inputCls, isEdit && "opacity-60 cursor-not-allowed")}
            value={slug}
            disabled={isEdit}
            onChange={(e) => {
              slugEditedRef.current = true
              setSlug(slugify(e.target.value))
            }}
            onBlur={() => {
              if (!slug.trim()) slugEditedRef.current = false
            }}
          />
          <div className="text-xs opacity-60 mt-1">
            {isEdit ? "Set when the tag was created and cannot be changed." : "Slug auto-fills from Name until you edit it."}
          </div>
        </div>

        <ColorPickerField value={color} onChange={setColor} />

        <div>
          <div className="text-sm opacity-80 mb-1">Category</div>
          <input className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)} />
        </div>

        <div>
          <div className="text-sm opacity-80 mb-1">Priority</div>
          <input type="number" className={inputCls} value={priority} onChange={(e) => setPriority(Number(e.target.value))} />
        </div>

        <div className="md:col-span-2">
          <div className="text-sm opacity-80 mb-1">Description</div>
          <textarea className={textareaCls} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button className={clsx("btn btn-primary", { "btn-disabled": saving || !canSave })} onClick={handleSave} type="button">
          {saving ? <span className="loading loading-spinner loading-sm" /> : "Save manual tag"}
        </button>
      </div>
    </div>
  )
}
